/**
 * "On view" lane — exhibitions, installations and displays that are currently
 * open, sorted by soonest closing.
 *
 * This is deliberately a SEPARATE page rather than a section of the events list.
 * A run spans weeks, so putting it in the day-by-day feed would either paint it
 * across a month of the calendar or (worse) show it once on its opening date and
 * then silently drop it. Keeping runs here means the normal list, calendar and
 * map stay exactly as they were — the only footprint on a normal day is a single
 * collapsed line on the events list that links to this page.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatRange } from '../lib/eventDates';
import { formatTag } from '../lib/tagTaxonomy';
import type { ExtractedEvent } from '../types';

/** Whole days from today until the run closes (0 = closes today). */
function daysUntil(iso: string | undefined): number | null {
  if (!iso) return null;
  const end = Date.parse(iso);
  if (!Number.isFinite(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86_400_000));
}

/**
 * Range label for a run, e.g. "Nov 1 – Dec 15, 2026".
 *
 * Defensive on purpose: this page exists to show dates, so one malformed stored
 * date must not blank the whole lane. `formatRange` throws a RangeError on
 * out-of-calendar instants.
 */
function runRangeLabel(e: ExtractedEvent): string {
  try {
    return formatRange(e, { date: { month: 'short', day: 'numeric', year: 'numeric' } });
  } catch {
    return '';
  }
}

function closingLabel(iso: string | undefined): string | null {
  const days = daysUntil(iso);
  if (days === null) return null;
  if (days === 0) return 'Closes today';
  if (days === 1) return 'Closes tomorrow';
  if (days <= 7) return `Closes in ${days} days`;
  return null;
}

export default function OnViewPage() {
  const [events, setEvents] = useState<ExtractedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.get<ExtractedEvent[]>('/api/events/on-view');
      setEvents(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load exhibitions.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">On view</h1>
          <p className="text-body-secondary small mb-0">
            Exhibitions, installations and displays that are open right now, closing soonest first.
          </p>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => void load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => void load()}>Retry</button>
        </div>
      )}

      {loading && events.length === 0 && !error && (
        <div className="text-body-secondary small py-4">Loading…</div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="text-body-secondary py-4">
          <p className="mb-1">Nothing is on view right now.</p>
          <p className="small mb-0">
            Exhibitions appear here automatically once a source announces one with an explicit
            &ldquo;on view&rdquo; date range.
          </p>
        </div>
      )}

      <div className="row g-3">
        {events.map((e) => {
          const closing = closingLabel(e.endUtc);
          return (
            <div className="col-12 col-md-6 col-xl-4" key={`${e.partitionKey}/${e.rowKey}`}>
              <div className="card h-100">
                {e.imageUrl && (
                  <img
                    src={e.imageUrl}
                    className="card-img-top"
                    alt=""
                    style={{ objectFit: 'cover', maxHeight: 180 }}
                    loading="lazy"
                  />
                )}
                <div className="card-body d-flex flex-column">
                  <h2 className="h6 card-title mb-1">{e.title}</h2>
                  {(e.venueName || e.locationText) && (
                    <div className="small text-body-secondary mb-1">
                      <i className="bi bi-geo-alt" aria-hidden="true" /> {e.venueName || e.locationText}
                    </div>
                  )}
                  <div className="small mb-2">
                    <i className="bi bi-calendar-range" aria-hidden="true" />{' '}
                    {runRangeLabel(e)}
                  </div>
                  {closing && (
                    <span className="badge text-bg-warning align-self-start mb-2">{closing}</span>
                  )}
                  {e.description && (
                    <p className="card-text small text-body-secondary mb-2">
                      {e.description.length > 180 ? `${e.description.slice(0, 177)}…` : e.description}
                    </p>
                  )}
                  <div className="mt-auto d-flex flex-wrap gap-1">
                    {(e.tags ?? [])
                      .filter((t) => ['art-exhibition', 'open-studios', 'museum', 'art-show', 'gallery-opening'].includes(t))
                      .slice(0, 3)
                      .map((t) => (
                        <span className="badge text-bg-light border" key={t}>{formatTag(t)}</span>
                      ))}
                    {e.rawUrl && (
                      <a
                        className="btn btn-sm btn-outline-secondary ms-auto"
                        href={e.rawUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        Source <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
